import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";

import { NAVIGATION_SECTIONS } from "../../shared/routes";

const SETUP_GROUP_ORDER = ["Products", "Business", "System"];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function matchesSearch(item, query) {
  const searchText = [item.name, item.group]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();
  return searchText.includes(query);
}

function NavigationLink({ item, nested = false, onNavigate }) {
  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={item.current ? "page" : undefined}
      className={classNames(
        "group relative flex min-h-10 items-center rounded-xl text-sm font-medium outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
        nested
          ? "ml-7 w-[calc(100%-1.75rem)] py-2 pl-3 pr-3"
          : "gap-3 px-3 py-2.5",
        item.current
          ? "bg-blue-50 text-blue-700"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-950",
      )}
    >
      {item.current ? (
        <span
          className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-blue-600"
          aria-hidden="true"
        />
      ) : null}
      {item.icon ? (
        <item.icon
          className={classNames(
            "h-5 w-5 shrink-0",
            item.current
              ? "text-blue-600"
              : "text-slate-400 group-hover:text-slate-600",
          )}
          aria-hidden="true"
        />
      ) : null}
      <span className="truncate">{item.name}</span>
    </Link>
  );
}

function NavigationGroup({ item, expanded, onToggle, onNavigate }) {
  const groupedChildren = useMemo(() => {
    const groups = new Map();
    item.children.forEach((child) => {
      const group = child.group ?? "";
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(child);
    });

    return [...groups.entries()].sort(([first], [second]) => {
      const firstIndex = SETUP_GROUP_ORDER.indexOf(first);
      const secondIndex = SETUP_GROUP_ORDER.indexOf(second);
      if (firstIndex === -1 || secondIndex === -1) return 0;
      return firstIndex - secondIndex;
    });
  }, [item.children]);

  const panelId = `navigation-panel-${item.id}`;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={classNames(
          "group relative flex min-h-10 w-full items-center gap-3 rounded-xl px-3 py-2.5 pr-10 text-left text-sm font-medium outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
          item.open
            ? "bg-blue-50 text-blue-700"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-950",
        )}
      >
        <item.icon
          className={classNames(
            "h-5 w-5 shrink-0",
            item.open
              ? "text-blue-600"
              : "text-slate-400 group-hover:text-slate-600",
          )}
          aria-hidden="true"
        />
        <span className="min-w-0 truncate">{item.name}</span>
        <ChevronRightIcon
          className={classNames(
            "absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-transform duration-200",
            expanded && "rotate-90",
          )}
          aria-hidden="true"
        />
      </button>

      <div
        id={panelId}
        className={classNames(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          expanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="pb-1 pt-1">
            {groupedChildren.map(([group, children]) => (
              <div key={group || "items"}>
                {group ? (
                  <p className="mb-1 mt-3 px-3 pl-10 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 first:mt-1">
                    {group}
                  </p>
                ) : null}
                {children.map((child) => (
                  <NavigationLink
                    key={child.id}
                    item={child}
                    nested
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NavigationMenu({ routes, onNavigate }) {
  const [query, setQuery] = useState("");
  const activeGroup = routes.find((route) => route.open)?.id ?? null;
  const [expandedId, setExpandedId] = useState(activeGroup);
  const normalizedQuery = query.trim().toLocaleLowerCase();

  useEffect(() => {
    if (activeGroup) setExpandedId(activeGroup);
  }, [activeGroup]);

  const filteredRoutes = useMemo(
    () =>
      routes
        .map((route) => {
          if (!normalizedQuery) return route;
          if (matchesSearch(route, normalizedQuery)) return route;

          const children = route.children?.filter((child) =>
            matchesSearch(child, normalizedQuery),
          );
          return children?.length ? { ...route, children } : null;
        })
        .filter(Boolean),
    [normalizedQuery, routes],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative mb-4 px-1">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        />
        <label htmlFor="navigation-search" className="sr-only">
          Search navigation
        </label>
        <input
          id="navigation-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search navigation"
          className="h-10 w-full rounded-xl border-0 bg-slate-100 py-2 pl-10 pr-3 text-sm text-slate-900 outline-none ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-600"
        />
      </div>

      <nav
        aria-label="Main navigation"
        className="min-h-0 flex-1 overflow-y-auto px-1 pb-6"
      >
        {NAVIGATION_SECTIONS.map((section) => {
          const sectionRoutes = filteredRoutes.filter(
            (route) => route.section === section.id,
          );
          if (!sectionRoutes.length) return null;

          return (
            <section
              key={section.id}
              aria-labelledby={`nav-${section.id}`}
              className="mb-5"
            >
              <h2
                id={`nav-${section.id}`}
                className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400"
              >
                {section.name}
              </h2>
              <div className="space-y-1">
                {sectionRoutes.map((route) =>
                  route.children ? (
                    <NavigationGroup
                      key={route.id}
                      item={route}
                      expanded={
                        Boolean(normalizedQuery) || expandedId === route.id
                      }
                      onToggle={() =>
                        setExpandedId((current) =>
                          current === route.id ? null : route.id,
                        )
                      }
                      onNavigate={onNavigate}
                    />
                  ) : (
                    <NavigationLink
                      key={route.id}
                      item={route}
                      onNavigate={onNavigate}
                    />
                  ),
                )}
              </div>
            </section>
          );
        })}

        {filteredRoutes.length === 0 ? (
          <div className="mx-2 rounded-xl border border-dashed border-slate-300 px-3 py-6 text-center text-sm text-slate-500">
            No navigation items match “{query}”.
          </div>
        ) : null}
      </nav>
    </div>
  );
}
